import { Grid2 as Grid, Paper, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import type { ProductDetail } from '../../api/business/types';
import { formatMoney, formatNumber } from '../../components/format';
import { useLanguage } from '../../i18n/LanguageContext';

type ProductLegalSectionProps = {
  product: ProductDetail;
  currency: string;
};

const Item = ({ label, children }: { label: string; children: ReactNode }) => (
  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Grid>
);

/**
 * The EU and legal properties of a product (R0030 WP-07): its condition and whether the shop prints it, its unit
 * price now — per unit, from the quantity in the pack — and the eco-tax included in its price.
 */
export const ProductLegalSection = ({ product, currency }: ProductLegalSectionProps) => {
  const { language, t } = useLanguage();
  const unit = product.unitLabel === null ? null : t(`unitSymbol.${product.unitLabel}`);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {t('legal.title')}
      </Typography>
      <Grid container spacing={2}>
        <Item label={t('legal.condition')}>
          {t(`condition.${product.condition}`)}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {product.showCondition ? t('legal.conditionShown') : t('legal.conditionHidden')}
          </Typography>
        </Item>
        <Item label={t('legal.unitPrice')}>
          {unit === null || product.unitQuantity === null ? (
            t('legal.noUnit')
          ) : (
            <>
              {product.unitPriceMinor === null
                ? t('common.none')
                : t('legal.unitPriceValue', {
                    price: formatMoney(product.unitPriceMinor, currency, language),
                    unit,
                  })}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('legal.packOf', { quantity: formatNumber(product.unitQuantity, language), unit })}
              </Typography>
            </>
          )}
        </Item>
        <Item label={t('legal.ecotaxLabel')}>
          {product.ecotaxMinor === 0
            ? t('legal.ecotaxNone')
            : formatMoney(product.ecotaxMinor, currency, language)}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('legal.ecotaxHint')}
          </Typography>
        </Item>
      </Grid>
    </Paper>
  );
};
